import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../components/Button'
import { StatusDot } from '../components/StatusDot'
import { api, type Campaign } from '../api/client'
import { fmtInt, fmtRelative } from '../lib/format'

const STATUS_TONE: Record<Campaign['status'], 'ok' | 'warn' | 'err' | 'muted' | 'active'> = {
  draft: 'muted',
  scheduled: 'active',
  running: 'warn',
  completed: 'ok',
  cancelled: 'muted',
  failed: 'err',
}

const STATUS_TEXT_TONE: Record<Campaign['status'], string> = {
  draft: 'text-paper-dim',
  scheduled: 'text-radar-cyan',
  running: 'text-amber-glow',
  completed: 'text-radar-green',
  cancelled: 'text-paper-trace',
  failed: 'text-radar-red',
}

export function CampaignsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<{ items: Campaign[] }>('/api/campaigns'),
    refetchInterval: 15_000,
  })
  const deleteDraft = useMutation({
    mutationFn: (id: number) => api.del<{ ok: boolean; id: number }>(`/api/campaigns/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    },
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Execution · email</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            Campaigns
            <span className="text-amber-glow"> ∙ </span>
            <span className="italic font-light">scheduled sends</span>
          </h1>
        </div>
        <Link to="/campaigns/new">
          <Button size="lg">+ New Campaign</Button>
        </Link>
      </header>

      {isLoading && <div className="label text-paper-trace p-6">Loading…</div>}
      {!isLoading && data && data.items.length === 0 && (
        <div className="label text-paper-trace p-6">
          No campaigns yet — create your first with <span className="label-amber">+ New Campaign</span>.
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="border border-ink-500 bg-ink-800/40">
          <table className="w-full text-[13px] font-mono">
            <thead>
              <tr className="border-b border-ink-500/60 bg-ink-800/60">
                <th className="label py-2 px-4 font-normal text-left">Campaign</th>
                <th className="label py-2 px-3 font-normal text-left">Schedule</th>
                <th className="label py-2 px-3 font-normal text-left">Next / Last fire</th>
                <th className="label py-2 px-3 font-normal text-right">Sent</th>
                <th className="label py-2 px-3 font-normal text-right">Renewals</th>
                <th className="label py-2 px-3 font-normal text-right">Revenue</th>
                <th className="label py-2 px-3 font-normal text-left">Status</th>
                <th className="label py-2 px-3 font-normal text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => {
                const hasFired = c.last_run_sent_count > 0 || !!c.last_fired_at
                return (
                  <tr key={c.id} className="border-b border-ink-500/30 hover:bg-ink-700/30">
                    <td className="px-4 py-2">
                      <Link to={`/campaigns/${c.id}`} className="text-paper hover:text-amber-glow">
                        {c.name}
                      </Link>
                      <div className="text-[10px] text-paper-trace">{c.slug}</div>
                    </td>
                    <td className="px-3 py-2 text-paper-dim">
                      {c.schedule_type === 'one_shot' ? 'One-shot' : (
                        <>Recurring · <span className="font-mono text-[11px]">{c.recurring_cron ?? '—'}</span></>
                      )}
                    </td>
                    <td className="px-3 py-2 text-paper-dim">
                      {c.next_fire_at ? fmtRelative(c.next_fire_at)
                        : c.last_fired_at ? `fired ${fmtRelative(c.last_fired_at)}`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-paper">
                      {fmtInt(c.last_run_sent_count)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {(c.conversions_count ?? 0) > 0 ? (
                        <span className="text-amber-glow">{fmtInt(c.conversions_count ?? 0)}</span>
                      ) : (
                        <span className="text-paper-trace">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {(c.conversions_revenue ?? 0) > 0 ? (
                        <span className="text-radar-green">${(c.conversions_revenue ?? 0).toFixed(2)}</span>
                      ) : (
                        <span className="text-paper-trace">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className="flex items-center gap-2">
                        <StatusDot tone={STATUS_TONE[c.status]} pulse={c.status === 'running'} />
                        <span className={`label ${STATUS_TEXT_TONE[c.status]}`}>{c.status}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {hasFired ? (
                        <Link
                          to={`/campaigns/${c.id}/results`}
                          className="label-amber hover:underline"
                        >
                          Results →
                        </Link>
                      ) : c.status === 'draft' ? (
                        <button
                          type="button"
                          disabled={deleteDraft.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete draft "${c.name || c.slug}"? This cannot be undone.`)) {
                              deleteDraft.mutate(c.id)
                            }
                          }}
                          className="label text-radar-red hover:underline disabled:opacity-50"
                        >
                          Delete
                        </button>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
